import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Table
} from "react-bootstrap";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import { rockLogsApi } from "../services/rockLogsApi";
import ErrorAlert from "../components/common/ErrorAlert";
import LoadingBlock from "../components/common/LoadingBlock";
import PaginationBar from "../components/common/PaginationBar";

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const CHART_MIN_HEIGHT = 280;

function parseDeviceInfo(raw) {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const o = JSON.parse(raw);
      return typeof o === "object" && o !== null && !Array.isArray(o) ? o : {};
    } catch {
      return {};
    }
  }
  return {};
}

function formatReason(reason) {
  if (reason == null) return "—";
  if (typeof reason === "object") {
    try {
      return JSON.stringify(reason);
    } catch {
      return String(reason);
    }
  }
  return String(reason);
}

function deviceDisplayName(info) {
  const n =
    info.device_name ||
    info.name ||
    info.model ||
    info.device_model ||
    info.product;
  return n ? String(n) : "—";
}

function deviceOsLabel(info) {
  const os = info.os || info.platform;
  const ver = info.os_version || info.system_version || info.version;
  if (os && ver) return `${os} ${ver}`;
  if (os) return String(os);
  if (ver) return String(ver);
  return "—";
}

function formatCreatedTime(row) {
  if (row?.created_at) {
    const d = new Date(row.created_at);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString();
  }
  if (typeof row?.created_time === "number" && row.created_time > 0) {
    return new Date(row.created_time * 1000).toLocaleString();
  }
  return "—";
}

function truncateReason(text, max = 100) {
  const s = text || "";
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

export default function ErrorLogsPage() {
  const [chartPoints, setChartPoints] = useState([]);
  const [barPoints, setBarPoints] = useState([]);
  const [barRangeLabel, setBarRangeLabel] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [chartsError, setChartsError] = useState(null);

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [appVersion, setAppVersion] = useState("");

  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({});
  const [tableLoading, setTableLoading] = useState(true);
  const [tableError, setTableError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setChartsLoading(true);
      setChartsError(null);
      try {
        const [line, bar, filters] = await Promise.all([
          rockLogsApi.getChart(),
          rockLogsApi.getBarChart(),
          rockLogsApi.getFilter()
        ]);
        if (cancelled) return;
        setChartPoints(line);
        setBarPoints(bar);
        setBarRangeLabel(bar[0]?.date_string || "");
        setFilterOptions(filters);
      } catch (e) {
        if (!cancelled) setChartsError(e);
      } finally {
        if (!cancelled) setChartsLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setTableLoading(true);
      setTableError(null);
      try {
        const normalized = await rockLogsApi.getLogs({
          page,
          items_per_page: itemsPerPage,
          q: searchQuery || undefined,
          app_version: appVersion || undefined
        });
        if (cancelled) return;
        setLogs(normalized.items);
        setMeta(normalized.meta);
      } catch (e) {
        if (!cancelled) setTableError(e);
      } finally {
        if (!cancelled) setTableLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [page, itemsPerPage, searchQuery, appVersion]);

  const pageSize = meta.items_per_page || itemsPerPage;

  const canNext = useMemo(() => {
    if (typeof meta.total === "number") {
      return page * pageSize < meta.total;
    }
    return (logs || []).length >= pageSize;
  }, [meta.total, page, pageSize, logs]);

  function applySearch(e) {
    e?.preventDefault?.();
    setPage(1);
    setSearchQuery(searchInput.trim());
  }

  function onPageSizeChange(e) {
    const n = Number(e.target.value);
    if (Number.isFinite(n) && n > 0) {
      setPage(1);
      setItemsPerPage(n);
    }
  }

  function onVersionChange(e) {
    setPage(1);
    setAppVersion(e.target.value);
  }

  const versionSelectOptions = useMemo(() => {
    if (filterOptions?.length) return filterOptions;
    return [{ label: "App version", value: "" }];
  }, [filterOptions]);

  const paginationRow = (
    <Row className="align-items-center gy-2">
      <Col>
        <div className="text-muted small">
          Page <b>{page}</b>
          {typeof meta.total === "number" ? (
            <>
              {" "}
              · Total <b>{meta.total}</b>
            </>
          ) : null}
        </div>
      </Col>
      <Col xs="auto">
        <PaginationBar
          page={page}
          onPageChange={(p) => setPage(Math.max(1, p))}
          canPrev={page > 1}
          canNext={canNext}
          showPageLabel={false}
        />
      </Col>
    </Row>
  );

  return (
    <div>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle">Monitoring</div>
              <h2 className="page-title">Error Logs</h2>
            </div>
          </div>
        </div>
      </div>

      <ErrorAlert error={chartsError} title="Failed to load charts" />

      <Row className="row-deck row-cards mb-3">
        <Col xs={12} md={6}>
          <Card>
            <Card.Header>
              <Card.Title className="mb-0">Errors by Date</Card.Title>
            </Card.Header>
            <Card.Body>
              {chartsLoading ? <LoadingBlock label="Loading chart…" /> : null}
              {!chartsLoading && chartPoints.length ? (
                <div style={{ width: "100%", height: CHART_MIN_HEIGHT }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={chartPoints}
                      margin={{ top: 8, right: 8, left: 0, bottom: 48 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e6e8e9" />
                      <XAxis
                        dataKey="date_string"
                        tick={{ fontSize: 11 }}
                        angle={-40}
                        textAnchor="end"
                        height={56}
                        interval="preserveStartEnd"
                      />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#206bc4"
                        strokeWidth={2}
                        dot={{ r: 3, fill: "#206bc4" }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : null}
              {!chartsLoading && !chartPoints.length ? (
                <div className="text-muted small py-4 text-center">No data for this period.</div>
              ) : null}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Card.Header>
              <Card.Title className="mb-0">Errors by Type</Card.Title>
              {barRangeLabel ? (
                <div className="text-muted small mt-1">{barRangeLabel}</div>
              ) : null}
            </Card.Header>
            <Card.Body>
              {chartsLoading ? <LoadingBlock label="Loading chart…" /> : null}
              {!chartsLoading && barPoints.length ? (
                <div style={{ width: "100%", height: CHART_MIN_HEIGHT }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={barPoints}
                      margin={{ top: 8, right: 8, left: 0, bottom: 56 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e6e8e9" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11 }}
                        angle={-40}
                        textAnchor="end"
                        height={64}
                        interval={0}
                      />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#d63384" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : null}
              {!chartsLoading && !barPoints.length ? (
                <div className="text-muted small py-4 text-center">No data for this period.</div>
              ) : null}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header>
          <Card.Title className="mb-0">Error Details</Card.Title>
        </Card.Header>
        <Card.Body>
          <Form className="mb-3" onSubmit={applySearch}>
            <Row className="g-2 align-items-end">
              <Col xs={12} md>
                <Form.Label className="small text-muted mb-1">Search</Form.Label>
                <InputGroup>
                  <Form.Control
                    placeholder="Search by error reason or code…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    aria-label="Search logs by reason or code"
                  />
                  <Button type="submit" variant="primary">
                    Search
                  </Button>
                </InputGroup>
              </Col>
              <Col xs={12} sm={6} md={3}>
                <Form.Label className="small text-muted mb-1">App version</Form.Label>
                <Form.Select
                  value={appVersion}
                  onChange={onVersionChange}
                  aria-label="Filter by app version"
                >
                  {versionSelectOptions.map((opt, i) => (
                    <option
                      key={`${String(opt.value)}-${opt.label}-${i}`}
                      value={opt.value ?? ""}
                    >
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} sm={6} md={2}>
                <Form.Label className="small text-muted mb-1">Page size</Form.Label>
                <Form.Select
                  value={String(itemsPerPage)}
                  onChange={onPageSizeChange}
                  aria-label="Items per page"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={String(n)}>
                      {n} per page
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </Form>

          <ErrorAlert error={tableError} title="Failed to load error logs" />
          {tableLoading ? <LoadingBlock label="Loading logs…" /> : null}

          {!tableLoading ? (
            <>
              <div className="border rounded-top px-3 py-2 bg-body-secondary bg-opacity-10">
                {paginationRow}
              </div>
              <div className="table-responsive border border-top-0">
                <Table striped hover className="table-vcenter mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 72 }}>ID</th>
                      <th style={{ minWidth: 220 }}>Reason</th>
                      <th style={{ width: 120 }}>Device name</th>
                      <th style={{ width: 120 }}>OS version</th>
                      <th style={{ width: 100 }}>App version</th>
                      <th style={{ width: 90 }}>Country</th>
                      <th style={{ width: 160 }}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(logs || []).map((row) => {
                      const id = row.id ?? row.log_id ?? "—";
                      const reasonFull = formatReason(row.reason);
                      const reasonShort = truncateReason(reasonFull);
                      const info = parseDeviceInfo(row.device_info);
                      return (
                        <tr key={id}>
                          <td className="text-muted small">{id}</td>
                          <td>
                            <span
                              className="small font-monospace text-break d-inline-block"
                              style={{ maxWidth: 480 }}
                              title={reasonFull.length > 100 ? reasonFull : undefined}
                            >
                              {reasonShort}
                            </span>
                          </td>
                          <td className="small">{deviceDisplayName(info)}</td>
                          <td className="small">{deviceOsLabel(info)}</td>
                          <td className="small">{row.app_version ?? "—"}</td>
                          <td className="small">{row.country_code ?? "—"}</td>
                          <td className="small text-muted">{formatCreatedTime(row)}</td>
                        </tr>
                      );
                    })}
                    {!logs?.length ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          No logs found.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </Table>
              </div>
              <div className="border rounded-bottom border-top-0 px-3 py-2 bg-body-secondary bg-opacity-10">
                {paginationRow}
              </div>
            </>
          ) : null}
        </Card.Body>
      </Card>
    </div>
  );
}
